import { useState } from 'react'
import type { ReactElement } from 'react'

import { useSession } from '../../auth/useSession'
import {
  Button,
  CodeLabel,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  ItemPicker,
  Modal,
  Spinner,
  Table,
  Tag,
} from '../../components'
import type { PickedItem, TableColumn } from '../../components'
import { FreshnessBadge } from '../../layout/FreshnessBadge'
import { getErrorCode } from '../../lib/errorCode'
import { trpcMessage } from '../../lib/trpcMessages'
import { MIRROR_REFETCH_MS, trpc } from '../../trpc'
import type { RouterOutputs } from '../../trpc'
import { AdminPageHeader, ReadOnlyNote, adminStyles as styles, changedFields, hasFields } from '../admin/AdminPage'
import { useConfigurationRefresh } from './useConfigurationRefresh'

type BmHomologation = RouterOutputs['configuration']['listBmHomologations']['homologations'][number]
type ItemReference = BmHomologation['wetacaSemi']

const bmErrorMessage = (error: unknown): string =>
  getErrorCode(error) === 'CONFLICT' ? 'Ese semi Wetaca ya está homologado.' : trpcMessage(error)

const picked = (reference: ItemReference | null): PickedItem | null =>
  reference === null
    ? null
    : { code: reference.code, title: reference.title, groupTitle: reference.groupTitle, known: reference.known }

const ReferenceCell = ({ reference }: { reference: ItemReference | null }): ReactElement =>
  reference === null
    ? <span className={styles['admin-page__muted']}>—</span>
    : <CodeLabel code={reference.code} title={reference.title} known={reference.known} />

/**
 * HOMOLOGACION BM: the dishes Wetaca and BM share, with the final product and
 * the semi of each brand. The planner reads one line per Wetaca semi, so a semi
 * homologated twice is flagged for somebody to resolve — the API refuses to
 * create a new one.
 */
export const BmHomologationsPage = (): ReactElement => {
  const { isReadOnly } = useSession()
  const utils = trpc.useUtils()
  const refreshStatus = useConfigurationRefresh()

  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<BmHomologation | null>(null)
  const [deleting, setDeleting] = useState<BmHomologation | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  const linesQuery = trpc.configuration.listBmHomologations.useQuery(undefined, { refetchInterval: MIRROR_REFETCH_MS })

  const afterWrite = (): void => {
    setActionError(null)
    void utils.configuration.listBmHomologations.invalidate()
    refreshStatus()
  }

  const remove = trpc.configuration.deleteBmHomologation.useMutation({
    onSuccess: () => {
      setDeleting(null)
      afterWrite()
    },
    onError: (error) => {
      setDeleting(null)
      setActionError(trpcMessage(error))
    },
  })

  const columns: Array<TableColumn<BmHomologation>> = [
    {
      key: 'wetacaSemi',
      header: 'Semi Wetaca',
      render: (line) => (
        <span className={styles['admin-page__tags']}>
          <ReferenceCell reference={line.wetacaSemi} />
          {line.duplicated && <Tag tone="warn" label="Duplicado" />}
        </span>
      ),
    },
    { key: 'bmSemi', header: 'Semi BM', render: (line) => <ReferenceCell reference={line.bmSemi} /> },
    { key: 'wetaca', header: 'Plato Wetaca', hideOnMobile: true, render: (line) => <ReferenceCell reference={line.wetaca} /> },
    { key: 'bm', header: 'Plato BM', hideOnMobile: true, render: (line) => <ReferenceCell reference={line.bm} /> },
    {
      key: 'actions',
      header: 'Acciones',
      align: 'right',
      render: (line) => (
        <div className={styles['admin-page__row-actions']}>
          <Button
            variant="secondary"
            disabled={isReadOnly}
            onClick={() => setEditing(line)}
            aria-label={`Editar ${line.wetacaSemiCode}`}
            label="Editar"
          />
          <Button
            variant="danger"
            disabled={isReadOnly}
            onClick={() => setDeleting(line)}
            aria-label={`Eliminar ${line.wetacaSemiCode}`}
            label="Eliminar"
          />
        </div>
      ),
    },
  ]

  let body: ReactElement
  if (linesQuery.isPending) {
    body = <Spinner label="Cargando homologaciones…" />
  } else if (linesQuery.isError) {
    body = <ErrorState error={linesQuery.error} onRetry={() => void linesQuery.refetch()} />
  } else if (linesQuery.data.homologations.length === 0) {
    body = <EmptyState title="Ninguna homologación BM" message="Ningún semi se comparte entre Wetaca y BM." />
  } else {
    const duplicated = linesQuery.data.homologations.filter((line) => line.duplicated).length
    body = (
      <>
        <div className={styles['admin-page__actions']}>
          <FreshnessBadge freshness={linesQuery.data.freshness} />
        </div>
        {duplicated > 0 && (
          <p className={`${styles['admin-page__note']} ${styles['admin-page__note--warn']}`}>
            {`Hay ${duplicated} líneas con un semi Wetaca repetido. El planificador solo usa una: elimina o corrige las que sobren.`}
          </p>
        )}
        <Table columns={columns} rows={linesQuery.data.homologations} rowKey={(line) => line.id} caption="Homologaciones BM" />
      </>
    )
  }

  return (
    <section className={styles['admin-page']}>
      <AdminPageHeader
        title="Homologación BM"
        intro="Los platos que Wetaca y BM comparten: el plato final y el semi de cada marca."
        actions={<Button disabled={isReadOnly} onClick={() => setCreating(true)} label="Nueva homologación" />}
      />
      {isReadOnly && <ReadOnlyNote />}
      {actionError !== null && <p role="alert" className={styles['admin-page__error']}>{actionError}</p>}

      {body}

      {creating && (
        <BmHomologationDialog
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false)
            afterWrite()
          }}
        />
      )}
      {editing !== null && (
        <BmHomologationDialog
          line={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            afterWrite()
          }}
        />
      )}

      <ConfirmDialog
        open={deleting !== null}
        variant="danger"
        title="Eliminar homologación"
        message={deleting === null ? '' : `Se eliminará la homologación del semi ${deleting.wetacaSemiCode}.`}
        confirmLabel="Eliminar"
        isLoading={remove.isPending}
        onCancel={() => setDeleting(null)}
        onConfirm={() => {
          if (deleting !== null) {
            remove.mutate({ id: deleting.id })
          }
        }}
      />
    </section>
  )
}

type BmHomologationDialogProps = {
  /** The line being edited; absent to create one. */
  line?: BmHomologation
  onClose: () => void
  onSaved: () => void
}

const BmHomologationDialog = ({ line, onClose, onSaved }: BmHomologationDialogProps): ReactElement => {
  const [wetacaSemi, setWetacaSemi] = useState<PickedItem | null>(picked(line?.wetacaSemi ?? null))
  const [bmSemi, setBmSemi] = useState<PickedItem | null>(picked(line?.bmSemi ?? null))
  const [wetaca, setWetaca] = useState<PickedItem | null>(picked(line?.wetaca ?? null))
  const [bm, setBm] = useState<PickedItem | null>(picked(line?.bm ?? null))
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const onError = (error: unknown): void => setErrorMessage(bmErrorMessage(error))
  const create = trpc.configuration.createBmHomologation.useMutation({ onSuccess: onSaved, onError })
  const update = trpc.configuration.updateBmHomologation.useMutation({ onSuccess: onSaved, onError })
  const isPending = create.isPending || update.isPending

  const draft = wetacaSemi === null
    ? null
    : {
      wetacaSemiCode: wetacaSemi.code,
      bmSemiCode: bmSemi?.code ?? null,
      wetacaCode: wetaca?.code ?? null,
      bmCode: bm?.code ?? null,
    }
  const patch = draft === null || line === undefined
    ? {}
    : changedFields({
      wetacaSemiCode: line.wetacaSemiCode,
      bmSemiCode: line.bmSemiCode,
      wetacaCode: line.wetacaCode,
      bmCode: line.bmCode,
    }, draft)
  const canSubmit = draft !== null && (line === undefined || hasFields(patch))

  return (
    <Modal open title={line === undefined ? 'Nueva homologación BM' : 'Editar homologación BM'} onClose={onClose} maxWidth="56rem">
      <form
        className={styles['admin-page__form']}
        noValidate
        onSubmit={(event) => {
          event.preventDefault()
          if (draft === null) {
            return
          }
          setErrorMessage(null)
          if (line === undefined) {
            create.mutate(draft)
          } else {
            update.mutate({ id: line.id, ...patch })
          }
        }}
      >
        <ItemPicker label="Semi Wetaca" selected={wetacaSemi} onSelect={setWetacaSemi} hint="Obligatorio: el semi que comparten." />
        <ItemPicker label="Semi BM" selected={bmSemi} onSelect={setBmSemi} clearable hint="Si BM lo fabrica con otro código." />
        <ItemPicker label="Plato Wetaca" selected={wetaca} onSelect={setWetaca} clearable />
        <ItemPicker label="Plato BM" selected={bm} onSelect={setBm} clearable />
        {errorMessage !== null && <p role="alert" className={styles['admin-page__error']}>{errorMessage}</p>}
        <div className={styles['admin-page__form-actions']}>
          <Button variant="secondary" onClick={onClose} disabled={isPending} label="Cancelar" />
          <Button
            type="submit"
            disabled={isPending || !canSubmit}
            label={line === undefined ? 'Crear homologación' : 'Guardar cambios'}
          />
        </div>
      </form>
    </Modal>
  )
}
